/**
 * The only place the web app talks to the API.
 *
 * Two things live here and nowhere else: how a session is stored and refreshed, and how an
 * error response is turned into something a screen can show. Spreading either around the app
 * is how three clients end up disagreeing about what "signed in" means.
 */

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? "http://localhost:5199";
const API_VERSION = "v1";

const SESSION_KEY = "commerceos.session";

export type Session = {
  accessToken: string;
  expiresAt: string;
  refreshToken: string;
  refreshExpiresAt: string;
};

export type CurrentUser = {
  userId: string;
  email: string;
  displayName: string;
  tenantId: string | null;
  roles: string[];
  /** What this user may do. The UI renders from this; it never decides for itself (ADR-0009). */
  permissions: string[];
  twoFactorEnabled: boolean;
};

/**
 * A challenge rather than a session: correct credentials, but something must happen first.
 * The `code` is the API's stable machine key, so we branch on that and never on the message.
 */
export type AuthChallenge = {
  code: "TWO_FACTOR_REQUIRED" | "TWO_FACTOR_ENROLMENT_REQUIRED" | "PASSWORD_CHANGE_REQUIRED";
  message: string;
  userId: string;
};

/**
 * A failed request, carrying the API's RFC 9457 Problem Details (ADR-0014).
 *
 * `code` is what the UI branches on. `fieldErrors` maps straight onto form inputs, which is
 * what lets the server own every validation rule while the form still highlights the right box.
 */
export class ApiError extends Error {
  // Declared explicitly rather than as constructor parameter properties: the project runs with
  // erasableSyntaxOnly, so type-only syntax that emits runtime code is rejected.
  readonly status: number;
  readonly code: string;
  readonly fieldErrors: Record<string, string[]>;
  readonly correlationId?: string;

  constructor(
    status: number,
    code: string,
    message: string,
    fieldErrors: Record<string, string[]> = {},
    correlationId?: string,
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.fieldErrors = fieldErrors;
    this.correlationId = correlationId;
  }

  /** The first message for a field, for rendering beneath an input. */
  fieldError(field: string): string | undefined {
    return this.fieldErrors[field]?.[0];
  }
}

export function loadSession(): Session | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    return raw ? (JSON.parse(raw) as Session) : null;
  } catch {
    // A private window or cleared storage. Treated as signed out, which is correct.
    return null;
  }
}

export function saveSession(session: Session): void {
  try {
    localStorage.setItem(SESSION_KEY, JSON.stringify(session));
  } catch {
    // The session still works for this page; it just will not survive a reload.
  }
}

export function clearSession(): void {
  try {
    localStorage.removeItem(SESSION_KEY);
  } catch {
    // Nothing useful to do, and nothing depends on it succeeding.
  }
}

async function toApiError(response: Response): Promise<ApiError> {
  let code = `HTTP_${response.status}`;
  let message = "Something went wrong.";
  let fieldErrors: Record<string, string[]> = {};
  let correlationId: string | undefined;

  try {
    const problem = await response.json();
    code = problem.code ?? code;
    message = problem.detail ?? problem.title ?? message;
    fieldErrors = problem.errors ?? {};
    correlationId = problem.correlationId;
  } catch {
    // Not a Problem Details body — a gateway error page, or nothing at all.
  }

  return new ApiError(response.status, code, message, fieldErrors, correlationId);
}

let refreshInFlight: Promise<Session | null> | null = null;

/**
 * Exchanges the refresh token for a new session.
 *
 * Deduplicated deliberately. Refresh tokens are single-use and rotated, so two concurrent
 * requests both refreshing would have the second replay an already-rotated token — which the
 * API correctly reads as a leak and responds to by killing the whole session.
 */
async function refreshSession(): Promise<Session | null> {
  if (refreshInFlight) {
    return refreshInFlight;
  }

  const current = loadSession();
  if (!current) {
    return null;
  }

  refreshInFlight = (async () => {
    try {
      const response = await fetch(`${API_BASE}/api/${API_VERSION}/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken: current.refreshToken }),
      });

      if (!response.ok) {
        clearSession();
        return null;
      }

      const session = (await response.json()) as Session;
      saveSession(session);
      return session;
    } catch {
      return null;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

type RequestOptions = {
  method?: string;
  body?: unknown;
  /** Skips the bearer token and the refresh-and-retry, for the auth endpoints themselves. */
  anonymous?: boolean;
};

/**
 * Makes a request, attaching the session and retrying once after a refresh on a 401.
 *
 * The retry is what stops a 15-minute access token from showing the user a spurious sign-in
 * screen mid-task.
 */
export async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = "GET", body, anonymous = false } = options;

  const send = async (token: string | null): Promise<Response> => {
    const headers: Record<string, string> = {};

    if (body !== undefined) {
      headers["Content-Type"] = "application/json";
    }
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    return fetch(`${API_BASE}/api/${API_VERSION}${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  };

  const session = anonymous ? null : loadSession();
  let response = await send(session?.accessToken ?? null);

  if (response.status === 401 && !anonymous && session) {
    const refreshed = await refreshSession();

    if (!refreshed) {
      clearSession();
      throw new ApiError(401, "UNAUTHENTICATED", "Your session has ended. Sign in again.");
    }

    response = await send(refreshed.accessToken);
  }

  if (!response.ok) {
    throw await toApiError(response);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}

/** Distinguishes a challenge from an issued session. */
function isChallenge(value: unknown): value is AuthChallenge {
  return (
    typeof value === "object" && value !== null && "code" in value && !("accessToken" in value)
  );
}

export type TwoFactorSetup = {
  /** The secret, grouped in fours, for anyone who cannot scan. */
  sharedKey: string;
  authenticatorUri: string;
  /** Inline SVG. Rendered server-side so the browser needs no QR library. */
  qrCodeSvg: string;
};

export const auth = {
  async signIn(email: string, password: string): Promise<Session | AuthChallenge> {
    const result = await apiFetch<Session | AuthChallenge>("/auth/sign-in", {
      method: "POST",
      body: { email, password },
      anonymous: true,
    });

    if (!isChallenge(result)) {
      saveSession(result);
    }

    return result;
  },

  async setPassword(
    userId: string,
    currentPassword: string,
    newPassword: string,
  ): Promise<Session | AuthChallenge> {
    const result = await apiFetch<Session | AuthChallenge>("/auth/set-password", {
      method: "POST",
      body: { userId, currentPassword, newPassword },
      anonymous: true,
    });

    if (!isChallenge(result)) {
      saveSession(result);
    }

    return result;
  },

  async twoFactor(userId: string, code: string): Promise<Session | AuthChallenge> {
    const result = await apiFetch<Session | AuthChallenge>("/auth/two-factor", {
      method: "POST",
      body: { userId, code },
      anonymous: true,
    });

    if (!isChallenge(result)) {
      saveSession(result);
    }

    return result;
  },

  /**
   * Starts authenticator enrolment.
   *
   * The password goes with it: a user id alone must never be enough to attach an authenticator
   * to somebody else's account.
   */
  beginTwoFactorSetup(userId: string, password: string): Promise<TwoFactorSetup> {
    return apiFetch<TwoFactorSetup>("/auth/two-factor/setup", {
      method: "POST",
      body: { userId, password },
      anonymous: true,
    });
  },

  async enableTwoFactor(
    userId: string,
    password: string,
    code: string,
  ): Promise<Session | AuthChallenge> {
    const result = await apiFetch<Session | AuthChallenge>("/auth/two-factor/enable", {
      method: "POST",
      body: { userId, password, code },
      anonymous: true,
    });

    if (!isChallenge(result)) {
      saveSession(result);
    }

    return result;
  },

  me(): Promise<CurrentUser> {
    return apiFetch<CurrentUser>("/auth/me");
  },

  async signOut(): Promise<void> {
    const session = loadSession();

    if (session) {
      try {
        await apiFetch<void>("/auth/sign-out", {
          method: "POST",
          body: { refreshToken: session.refreshToken },
          anonymous: true,
        });
      } catch {
        // Ending the local session matters more than the server acknowledging it.
      }
    }

    clearSession();
  },

  isChallenge,
};
